import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { CommonCodeColumn, CompCdColumn } from "src/utils/grid/GridColumnContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatNumber } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";
import {
	PRD5010_R01ProductResultDto,
	defaultPRD5010_R01ProductResultDto,
} from "./Dto_PRD5010_R01";
import { usePRD5010_R01Store } from "./Store_PRD5010_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = "PRD5010_R01-product-result-grid-column-state";

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD5010_R01ProductResultDto> | null>;
}

export const ProductResultTable_PRD5010_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		PRD5010_R01ProductResultList,
		selectedPRD5010_R01ProductResultList,
		setSelectedPRD5010_R01ProductResultList,
		initSelectedPRD5010_R01ProductResultList,
		productResultColumnState,
		setProductResultColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = usePRD5010_R01Store();

	const {
		jobTeamList,
		ordTypeList,
		unitList,
		compCdList,
		employeeCdList,
		workPlaceList,
	} = useCommonCodeStore();

	const codeMaps = useMemo(() => ({
		ordType: new Map(ordTypeList.map(v => [v.minorCd, v.codeNm])),
		workPlace: new Map(workPlaceList.map(v => [v.workplaceCd, v.workplaceNm])),
		unit: new Map(unitList.map(v => [v.minorCd, v.codeNm])),
		emp: new Map(employeeCdList.map(v => [v.empNo, v.empNm])),
		comp: new Map(compCdList.map(v => [v.compCd, v.compNm])),
	}), [ordTypeList, workPlaceList, unitList, employeeCdList, compCdList]);

	const getRowKey = useCallback((row: PRD5010_R01ProductResultDto) => row.productResultNo, []);

	const {
		settingRowIndex,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: PRD5010_R01ProductResultList || [],
		defaultGridData: defaultPRD5010_R01ProductResultDto,
		selectedRow: selectedPRD5010_R01ProductResultList,
	});

	const getRowIdForGrid = useCallback((params: any) => {
		const id = params.data?.valueIndex ?? params.id;
		return String(id);
	}, []);

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		productResultNo: "생산실적번호",
		compCd: "사업장",
		compNm: "사업장명",
		productDt: "생산일자",
		orderType: "지시구분",
		workplaceCd: "작업장",
		workplaceNm: "작업장명",
		jobTeam: "작업조",
		itemCd: "품목코드",
		itemStatus: "품목상태",
		itemNm: "품명",
		itemSpec: "규격",
		itemUnit: "단위",
		productOrderNo: "생산지시번호",
		orderQty: "지시수량",
		productEmp: "작업자",
		productEmpNm: "작업자명",
		productQty: "생산수량",
		badQty: "불량수량",
		acceptQty: "입고수량",
		remark: "비고",
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'productResultNo')(params), []);

	const baseColumnDefs: ColDef<PRD5010_R01ProductResultDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "생산실적번호", colId: "productResultNo", field: "productResultNo", width: 150,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productResultNo'),
		},
		{
			headerName: "사업장", colId: "compCd", field: "compCd", width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: "생산일자", colId: "productDt", field: "productDt", width: 100,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productDt'),
			valueFormatter: (params: any) =>
				params.data?.productDt ? formatDate(new Date(params.data.productDt)) : "",
			comparator: cellDateComparator,
		},
		{
			headerName: "지시구분", colId: "orderType", field: "orderType", width: 90,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'orderType'),
			valueGetter: (params: any) => codeMaps.ordType.get(params.data?.orderType) || "",
		},
		{
			headerName: "작업장", colId: "workplaceCd", field: "workplaceCd", width: 110,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'workplaceCd'),
			valueGetter: (params: any) =>
				codeMaps.workPlace.get(params.data?.workplaceCd) || params.data?.workplaceNm || "",
		},
		{
			headerName: "작업조", colId: "jobTeam", field: "jobTeam", width: 90,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'jobTeam'),
			...CommonCodeColumn.options(jobTeamList, false, undefined, undefined),
		},
		{
			headerName: "품목코드", colId: "itemCd", field: "itemCd", width: 110,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: "품명", colId: "itemNm", field: "itemNm", width: 160,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 120,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: "단위", colId: "itemUnit", field: "itemUnit", width: 70,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemUnit'),
			valueGetter: (params: any) => codeMaps.unit.get(params.data?.itemUnit) || "",
		},
		{
			headerName: "품목상태", colId: "itemStatus", field: "itemStatus", width: 90,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemStatus'),
		},
		{
			headerName: "지시수량", colId: "orderQty", field: "orderQty", width: 90, type: "numericColumn",
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'orderQty'),
			valueFormatter: (params: any) => formatNumber(params.value),
		},
		{
			headerName: "작업자", colId: "productEmp", field: "productEmp", width: 90,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productEmp'),
		},
		{
			headerName: "작업자명", colId: "productEmpNm", field: "productEmpNm", width: 90,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productEmpNm'),
			valueGetter: (params: any) =>
				params.data?.productEmpNm || codeMaps.emp.get(params.data?.productEmp) || "",
		},
		{
			headerName: "생산수량", colId: "productQty", field: "productQty", width: 90, type: "numericColumn",
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'productQty'),
			valueFormatter: (params: any) => formatNumber(params.value),
		},
		{
			headerName: "불량수량", colId: "badQty", field: "badQty", width: 90, type: "numericColumn",
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'badQty'),
			valueFormatter: (params: any) => formatNumber(params.value),
		},
		{
			headerName: "입고수량", colId: "acceptQty", field: "acceptQty", width: 90, type: "numericColumn",
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'acceptQty'),
			valueFormatter: (params: any) => formatNumber(params.value),
		},
		{
			headerName: "비고", colId: "remark", field: "remark", flex: 1, minWidth: 120,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [getCellStyle, compCdList, jobTeamList, codeMaps, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PRD5010_R01ProductResultDto>[] = useMemo(() => {
		if (productResultColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<PRD5010_R01ProductResultDto>({
			baseColumnDefs,
			columnState: productResultColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [productResultColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const getRowStyleForBottom = useCallback((params: any) => {
		if (params.node.rowPinned === "bottom") {
			return { background: "#F0F0F0", fontWeight: "bold", borderTop: "none" };
		}
		return undefined;
	}, []);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		selectedIds: selectedPRD5010_R01ProductResultList as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) =>
			setSelectedPRD5010_R01ProductResultList(ids.map(String)),
		resolveId: (row: PRD5010_R01ProductResultDto) => getRowKey(row),
	}), [selectedPRD5010_R01ProductResultList, setSelectedPRD5010_R01ProductResultList, getRowKey]);

	const pinnedBottomRow = useMemo(() => [{
		rowIndex: "",
		productResultNo: `${PRD5010_R01ProductResultList.length.toLocaleString()}건`,
		productQty: PRD5010_R01ProductResultList.reduce((sum, item) => sum + (item.productQty || 0), 0),
		badQty: PRD5010_R01ProductResultList.reduce((sum, item) => sum + (item.badQty || 0), 0),
		acceptQty: PRD5010_R01ProductResultList.reduce((sum, item) => sum + (item.acceptQty || 0), 0),
		editable: false,
	}], [PRD5010_R01ProductResultList]);

	useEffect(() => { ensureSelectedRow(); }, [PRD5010_R01ProductResultList, selectedPRD5010_R01ProductResultList]);

	const rowClassRules = useMemo(() => ({
		selected: (params: any) => {
			if (!params.data) return false;
			return selectedPRD5010_R01ProductResultList.includes(getRowKey(params.data));
		},
	}), [selectedPRD5010_R01ProductResultList, getRowKey]);

	const handleRowDragEnd = useCallback((e: any) => {
		const api = gridRef.current?.api || e?.api;
		if (!api) return;

		if (api.applyColumnState) {
			api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
		}
	}, [gridRef]);

	return (
		<div className="flex flex-col gap-1 h-full min-h-0">
			{/* <SmallTitleSpan>생산실적</SmallTitleSpan> */}
			<div className="flex flex-col gap-3 flex-1 min-h-0">
				<GridContextWrapper<PRD5010_R01ProductResultDto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={PRD5010_R01ProductResultList}
					initSelected={initSelectedPRD5010_R01ProductResultList}
					defaultRowData={defaultPRD5010_R01ProductResultDto}
					baseColumnDefs={productResultColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setProductResultColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Select<PRD5010_R01ProductResultDto>
						ref={gridRef}
						rowData={PRD5010_R01ProductResultList}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowIdForGrid}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={COLUMN_STATE_KEY}
						setColumnState={setProductResultColumnState}
						onGridReady={(params) => {
							params.api.getDisplayedRowCount();
							params.api.getRenderedNodes();
						}}
						shiftRangeSelection={shiftRangeSelectionConfig}
						pinnedBottomRowData={pinnedBottomRow}
						rowClassRules={rowClassRules}
						getRowStyle={getRowStyleForBottom}
						rowDragManaged={true}
						rowDragEntireRow={true}
						onRowDragEnd={handleRowDragEnd}
						isRowSelectable={(node) => !!node.data}
						suppressRowClickSelection={false}
						ensureDomOrder={true}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
